import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { updateDeveloperListing, type DeveloperApp } from "./apps.api";

const KNOWN_TAGS = ["productivity", "communication", "navigation", "ai", "games", "developer", "media"];

/**
 * Store listing copy. Every field is an override: blank means "use the
 * value from miniapp.json", which the placeholder shows.
 */
export function ListingCard({ app }: { app: DeveloperApp }) {
  const qc = useQueryClient();
  const override = app.listing ?? { tagline: null, description: null, tags: null, featured: false, featuredAt: null };
  const manifest = app.manifestListing ?? { tagline: null, description: null, tags: null };
  const [tagline, setTagline] = useState(override.tagline ?? "");
  const [description, setDescription] = useState(override.description ?? "");
  const [tags, setTags] = useState(override.tags?.join(", ") ?? "");
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setTagline(override.tagline ?? "");
    setDescription(override.description ?? "");
    setTags(override.tags?.join(", ") ?? "");
  }, [override.tagline, override.description, override.tags]);

  const save = useMutation({
    mutationFn: () =>
      updateDeveloperListing(app.packageName, {
        tagline: tagline.trim() || null,
        description: description.trim() || null,
        tags: splitTags(tags),
      }),
    onSuccess: async () => {
      setSaved(true);
      await qc.invalidateQueries({ queryKey: ["developer-apps"] });
    },
  });

  const dirty =
    (tagline.trim() || null) !== override.tagline ||
    (description.trim() || null) !== override.description ||
    JSON.stringify(splitTags(tags)) !== JSON.stringify(override.tags);

  return (
    <Card className="mt-6 rounded-[16px] border-[#e0e4de] bg-white py-0 shadow-[0_1px_2px_rgba(20,21,27,0.06)] sm:rounded-[18px]">
      <CardHeader className="border-b border-[#eceeeb] px-4 py-4 sm:px-6 sm:py-5">
        <CardTitle className="font-display text-[18px]">Store listing</CardTitle>
        <p className="text-sm text-[#747780]">
          Overrides the copy in <code className="rounded bg-[#f0f2ef] px-1 font-mono text-[12px]">miniapp.json</code>. Leave a field blank to use the manifest value shown as its placeholder.
          {override.featured ? <span className="ml-2 rounded-full bg-[#e9f8f1] px-2 py-0.5 text-xs font-medium text-[#087d50]">Featured</span> : null}
        </p>
      </CardHeader>
      <CardContent className="grid gap-4 p-4 sm:p-6">
        <div className="grid gap-1.5">
          <Label htmlFor="listing-tagline">Tagline</Label>
          <Input
            id="listing-tagline"
            value={tagline}
            maxLength={80}
            placeholder={manifest.tagline ?? "One line under the name"}
            onChange={event => { setTagline(event.target.value); setSaved(false); }}
          />
          <span className="text-xs text-[#a0a3aa]">{tagline.length}/80</span>
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="listing-description">Description</Label>
          <Textarea
            id="listing-description"
            value={description}
            rows={5}
            maxLength={4000}
            placeholder={manifest.description ?? app.description ?? "What the miniapp does, shown on the store page"}
            onChange={event => { setDescription(event.target.value); setSaved(false); }}
          />
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="listing-tags">Tags</Label>
          <Input
            id="listing-tags"
            value={tags}
            placeholder={manifest.tags?.join(", ") ?? "productivity, ai"}
            onChange={event => { setTags(event.target.value); setSaved(false); }}
          />
          <span className="text-xs text-[#a0a3aa]">Comma-separated lowercase slugs, up to 5. Filter chips: {KNOWN_TAGS.join(", ")}.</span>
        </div>
        {save.error ? <p className="text-sm text-[#a64235]">{save.error instanceof Error ? save.error.message : "Could not save the listing"}</p> : null}
        <div className="flex items-center gap-3">
          <Button className="rounded-full bg-[#111217] text-white hover:bg-[#25262c]" disabled={!dirty || save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? <Loader2 className="size-4 animate-spin" /> : null}
            Save listing
          </Button>
          {saved && !dirty ? <span className="text-sm text-[#087d50]">Saved</span> : null}
        </div>
      </CardContent>
    </Card>
  );
}

function splitTags(value: string): string[] | null {
  const tags = value
    .split(",")
    .map(tag => tag.trim().toLowerCase())
    .filter(Boolean);
  return tags.length > 0 ? [...new Set(tags)] : null;
}
